import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import CountUp from "react-countup";
import { numberFormatter, round, roundWithComma } from "app/utils/stringUtil";
import {CountUpUtil} from "app/utils/countUpUtil";
import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {ResponseResultCode} from "app/model/response-api";
import NoDataToDisplay from "highcharts/modules/no-data-to-display";
import {HighchartsOptionsUtil} from "app/utils/highchartsOptionsUtil";
import DashboardLoading from "./DashboardLoading";
import {DbUserService} from "app/services/db/dbUserService";
import DashboardCard from "./DashboardCard";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
NoDataToDisplay(Highcharts);

const graphA: any = {
  chart: {
    height: 270,
    spacingTop: 0,
    spacingBottom: 0,
    spacingLeft: 0,
    spacingRight: 0,
  },
  colors: ['#44C5DB', '#4C96F9'],
  title: { text: '' },
  exporting: {enabled: false},
  credits: {enabled: false},
  noData: HighchartsOptionsUtil.noData,
  legend: {
    layout: 'horizontal',
    verticalAlign: 'top',
    borderWidth: 0,
    symbolHeight: 10,
    symbolPadding: 5,
    itemDistance: 20,
    itemMarginBottom: 10,
    labelFormat: `<span style="font-size: 0.75rem;">{name}</span>`,
  },
  subtitle: {
    align: 'center',
    verticalAlign: 'middle',
    y: 35,
    useHTML: true,
  },
  tooltip: {
    formatter: function (this: any) {
      return `<span style="color: ${this.color};">●&nbsp;</span>${this.point.name}: <strong style="font-size: 0.75rem;">${roundWithComma(this.point.y, 2)}</strong>kWh (${round(this.point.percentage)}%)`;
    },
    style: {
      fontSize: '0.75rem'
    }
  },
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: 'pointer',
      innerSize: "72%",
      borderRadius: 0,
    },
    series: {
      showInLegend: true,
      borderWidth: 0.5,
      dataLabels: {
        enabled: false,
      },
    },
  },
  series: [{
    name: '공급 전력',
    type: 'pie',
    data: []
  }],
};

interface PwrSupplyModel {
  pwrUse: number;
  pwrGen: number;
  pwrTot: number;
  pwrUseRt: number;
  pwrGenRt: number;
}

export const makeCompareUpDownBadge = (value: any) => {
  if (value != undefined && !isNaN(value)) {
    if (Number(value) > 0) {
      return (<span>어제보다<em className="up"></em>{round(Math.abs(value), 0)}%</span>);
    } else if (Number(value) < 0) {
      return (<span>어제보다<em className="down"></em>{round(Math.abs(value), 0)}%</span>);
    } else {
      return (<span>어제와 동일</span>);
    }
  }
  return (<span>-</span>);
}

export default function EnergySplyStatus(props: Readonly<DashboardModuleProps>) {
  const [chartA, setChartA] = useState<any>();
  const [pwrSupply, setPwrSupply] = useState<PwrSupplyModel>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    DbUserService.getInstance().fetchPowerSupplyInfo().then(response => {
      if (response && response.resultCode == ResponseResultCode.Y && response.data) {
        const pwrUse = response.data.pwrUse ?? 0;
        const pwrGen = response.data.pwrGen ?? 0;
        const pwrUsePrev = response.data.pwrUsePrev ?? 0;
        const pwrGenPrev = response.data.pwrGenPrev ?? 0;

        setPwrSupply({
          pwrUse: pwrUse,
          pwrGen: pwrGen,
          pwrTot: pwrUse + pwrGen,
          pwrUseRt: pwrUse !== 0 && pwrUsePrev === 0 ? 100 : Math.round((pwrUse - pwrUsePrev) / pwrUsePrev * 100),
          pwrGenRt: pwrGen !== 0 && pwrGenPrev === 0 ? 100 : Math.round((pwrGen - pwrGenPrev) / pwrGenPrev * 100),
        });
      } else {
        toast.error('데이터를 불러오지 못했습니다.. 다시 시도해 주시기 바랍니다.');
      }
    }).catch(() => {
      toast.error('시스템 오류가 발생했습니다. 다시 시도해 주시기 바랍니다.');
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    createChart();

    const handleResize = () => {
      createChart();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [pwrSupply]);

  function createChart() {
    let seriesData: any[] = [];
    let subtitleA = {};
    let borderW = 0;

    if (pwrSupply) {
      seriesData = [
        {
          name: 'KEPCO',
          y: pwrSupply.pwrUse
        }, {
          name: '재생에너지',
          y: pwrSupply.pwrGen
        }
      ];

      subtitleA = {
        align: 'center',
        verticalAlign: 'middle',
        y: 35,
        useHTML: true,
        text: `
          <div style="text-align: center;">
            <span style="color: #333; font-size: 1.2rem; font-weight: 800;">
              ${pwrSupply?.pwrGen && pwrSupply?.pwrTot ? numberFormatter((pwrSupply?.pwrGen/pwrSupply.pwrTot) * 100, 1) : 0}
            </span>
            <span style="color: #555; font-size: 0.75rem; font-weight: 500;">%</span>
          </div>
        `,
      };
      borderW = (pwrSupply.pwrTot === 0 && pwrSupply.pwrGen === 0) ? 0.5 : 0;
    }

    setChartA({
      ...graphA,
      subtitle: subtitleA,
      series: {
        type: 'pie',
        name: '공급 전력',
        data: seriesData,
        borderWidth: borderW,
      }
    });
  }

  return (
    <DashboardCard
      title="금일 에너지 자립률"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/pc/eu/supplyconsumption"
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      <div className="dash-box-flex">
        <div className='chart'>
          {chartA && (
            <HighchartsReact highcharts={Highcharts} options={chartA}/>
          )}
        </div>
        <div className="flex-col2">
          <div className="box-style11 pd0">
            <div className="box-in">
              <span>총 공급 전력</span>
              <span>
                <strong>
                  <CountUp
                    end={round(pwrSupply?.pwrTot ?? 0, 2)}
                    decimals={CountUpUtil.getDecimal(round(pwrSupply?.pwrTot ?? 0, 2))}
                    duration={0.5}
                  />
                </strong>
                <em>kWh</em>
              </span>
            </div>
          </div>
          <span>금일 전력 공급량</span>
          <div className="list">
            <span>KEPCO</span>
            <span>
              {makeCompareUpDownBadge(pwrSupply?.pwrUseRt)}
              <strong>{roundWithComma(pwrSupply?.pwrUse ?? 0, 2)}</strong>
              <em>kWh</em>
            </span>
          </div>
          <div className="list">
            <span>재생에너지</span>
            <span>
              {makeCompareUpDownBadge(pwrSupply?.pwrGenRt)}
              <strong>{roundWithComma(pwrSupply?.pwrGen ?? 0, 2)}</strong>
              <em>kWh</em>
            </span>
          </div>
        </div>
      </div>
    </DashboardCard>
  );
}